'use client';

import { faLoader } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, InputField, Modal, ModalClose, ModalContent, ModalDescription, ModalHeading } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';
import moment from 'moment';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { IsManagerDetails } from '@/legacy/utils/tournaments.types';
import { Event, TournamentEvent } from '@/tournament/components/events/Event';

export interface EventGroup {
	groupTitle: string;
	events: TournamentEvent[];
}

const getDistinctDates = (groups: EventGroup[]): string[] => {
	const dateSet = new Set();
	let array: string[] = [];

	groups?.forEach((group) => {
		group.events.forEach((event) => {
			if (event.date) {
				dateSet.add(event.date);
			}
		});
	});

	array = Array.from(dateSet).sort((a, b) =>
		moment(a as string, 'MMM DD, YYYY').isBefore(moment(b as string, 'MMM DD, YYYY')) ? -1 : 1
	) as string[];

	return array;
};

const getDistinctStatuses = (groups: EventGroup[]): string[] => {
	const statusSet = new Set<string>();
	let array: string[] = [];

	groups.forEach((group) => {
		group.events.forEach((event) => {
			statusSet.add(event.status.text);
		});
	});

	array = Array.from(statusSet);

	return array;
};

export const EventList = ({ slug, isManagerDetails }: { slug: string; isManagerDetails: IsManagerDetails }) => {
	const searchParams = useSearchParams();

	const needAPartnerParam = searchParams.get('needAPartner');
	const router = useRouter();

	const [isLoading, setIsLoading] = useState(true);

	const [tourneyId, setTourneyId] = useState<string>('');
	const [tourneyPlayersHidden, setTourneyPlayersHidden] = useState(false);

	const [initialEventsData, setInitialEventsData] = useState<EventGroup[]>();
	const [filteredEventsData, setFilteredEventsData] = useState<EventGroup[]>();
	const [isEventDropdownOpen, setIsEventDropdownOpen] = useState<boolean>(false);

	const [filterDates, setFilterDates] = useState<string[]>();
	const [isDatesDropdownOpen, setIsDatesDropdownOpen] = useState<boolean>(false);

	const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
	const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState<boolean>(false);

	const [activeDateFilter, setActiveDateFilter] = useState<string>('');
	const [activeEventFilter, setActiveEventFilter] = useState<TournamentEvent>();
	const [activeStatusFilter, setActiveStatusFilter] = useState<string>('');

	const [searchTerm, setSearchTerm] = useState<string>('');

	const [needAPartnerOnly] = useState<boolean>(needAPartnerParam === 'true');

	const getTournamentEvents = async () => {
		setIsLoading(true);
		const response = await fetch(`/tournaments/api/tourneyEvents?slug=${slug}&searchTerm=${searchTerm}&needAPartnerOnly=${needAPartnerOnly}`);

		const eventsRes = (await response.json()) as { tourneyId: string; events: EventGroup[]; tourneyPlayersHidden: boolean };

		setInitialEventsData(eventsRes.events);
		setFilteredEventsData(eventsRes.events);

		setFilterDates(getDistinctDates(eventsRes.events));
		setFilterStatuses(getDistinctStatuses(eventsRes.events));

		setTourneyId(eventsRes.tourneyId);
		setTourneyPlayersHidden(eventsRes.tourneyPlayersHidden);
		setIsLoading(false);
	};

	const debouncedSearch = useRef(
		_debounce(async (value: string) => {
			setSearchTerm(value);
		}, 75)
	).current;

	async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		debouncedSearch(e.target.value);
	}

	useEffect(() => {
		getTournamentEvents();

		const params = new URLSearchParams(window.location.search);

		if (needAPartnerOnly) params.set('needAPartner', 'true');
		else if (needAPartnerParam) params.set('needAPartner', 'false');
		else params.delete('needAPartner');

		router.push(`${window.location.pathname}?${params.toString()}`);
	}, [needAPartnerOnly]);

	useEffect(() => {
		if (activeDateFilter) {
			const filteredEvents = initialEventsData
				?.map((group) => ({
					groupTitle: group.groupTitle,
					events: group.events.filter(
						(event: TournamentEvent) =>
							moment.utc(event.date).isSame(moment.utc(activeDateFilter), 'day') ||
							event.multipleDates.some((multipleDate) => moment(multipleDate.value).isSame(moment(activeDateFilter), 'day'))
					)
				}))
				.filter((group) => group.events.length > 0);

			setFilteredEventsData(filteredEvents);
		} else {
			setFilteredEventsData(initialEventsData);
		}
	}, [activeDateFilter]);

	useEffect(() => {
		if (activeEventFilter) {
			const filteredEvents = initialEventsData
				?.map((group) => ({
					groupTitle: group.groupTitle,
					events: group.events.filter((event: TournamentEvent) => event.activityId === activeEventFilter.activityId)
				}))
				.filter((group) => group.events.length > 0);

			setFilteredEventsData(filteredEvents);
		} else {
			setFilteredEventsData(initialEventsData);
		}
	}, [activeEventFilter]);

	useEffect(() => {
		if (activeStatusFilter) {
			const filteredEvents = initialEventsData
				?.map((group) => ({
					groupTitle: group.groupTitle,
					events: group.events.filter((event: TournamentEvent) => event.status.text === activeStatusFilter)
				}))
				.filter((group) => group.events.length > 0);

			setFilteredEventsData(filteredEvents);
		} else {
			setFilteredEventsData(initialEventsData);
		}
	}, [activeStatusFilter]);

	const datesFilterRef = useRef<HTMLDivElement>(null);
	const eventsFilterRef = useRef<HTMLDivElement>(null);
	const statusFilterRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				!datesFilterRef.current?.contains(event.target) &&
				!eventsFilterRef.current?.contains(event.target) &&
				!statusFilterRef.current?.contains(event.target)
			) {
				setIsDatesDropdownOpen(false);
				setIsEventDropdownOpen(false);
				setIsStatusDropdownOpen(false);
			}
		};

		window.addEventListener('mousedown', handleClickOutside);

		return () => {
			window.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const [bracketModalOpen, setBracketModalOpen] = useState<boolean>(false);

	return (
		<>
			{isLoading ? (
				<div className="flex h-48 flex-col items-center justify-center gap-4">
					<FontAwesomeIcon className="text-4xl font-extralight" icon={faLoader} spin />
					<div>Loading events...</div>
				</div>
			) : (
				<div className="flex flex-col gap-6 bg-transparent">
					<div className="w-fit rounded-2xl border border-blue-400 px-4 py-1 text-sm font-semibold text-blue-600">
						All start times are subject to change and events might be merged or split based on event registration.
					</div>

					<div className="flex flex-row items-center gap-2">
						<div className="w-full">
							<InputField
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										getTournamentEvents();
									}
								}}
								placeholder="Search for a Player"
								className=" !border-gray-300 !shadow-blue-100"
								PrefixIcon={() => <FontAwesomeIcon icon={faSearch} />}
								onChange={handleChange}
							/>
						</div>
						<Button className="bg-white" onClick={() => getTournamentEvents()} variant="secondary">
							Search
						</Button>
					</div>
					<div className="flex flex-row flex-wrap items-center gap-2 md:flex-nowrap">
						<div className="w-full md:w-1/3">
							<div className="relative">
								<Button
									variant="link-gray"
									suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
									className="w-full justify-between rounded-lg border border-gray-300 bg-white p-2 text-left text-sm font-normal text-gray-500"
									onClick={() => {
										setIsDatesDropdownOpen(!isDatesDropdownOpen);
										setIsEventDropdownOpen(false);
										setIsStatusDropdownOpen(false);
									}}
								>
									{activeDateFilter || 'Show all dates'}
								</Button>
								{isDatesDropdownOpen && (
									<div
										ref={datesFilterRef}
										className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white text-sm"
									>
										<ul>
											<li
												className={clsx(
													'cursor-pointer px-4 py-2 text-gray-500 hover:bg-gray-100',
													!activeDateFilter && 'bg-blue-50'
												)}
												onClick={() => {
													setActiveDateFilter('');
													setIsDatesDropdownOpen(false);
												}}
											>
												Show all dates
											</li>

											{filterDates?.map((date, index) => (
												<li
													key={index}
													className={clsx(
														'cursor-pointer px-4 py-2 text-gray-500 hover:bg-gray-100',
														activeDateFilter === date && 'bg-blue-50'
													)}
													onClick={() => {
														setActiveDateFilter(date);
														setIsDatesDropdownOpen(false);
													}}
												>
													{date}
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						</div>
						<div className="relative w-full md:w-1/3">
							<div className="relative">
								<Button
									variant="link-gray"
									suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
									className="w-full justify-between rounded-lg border border-gray-300 bg-white p-2 text-left text-sm font-normal text-gray-500"
									onClick={() => {
										setIsEventDropdownOpen(!isEventDropdownOpen);
										setIsDatesDropdownOpen(false);
										setIsStatusDropdownOpen(false);
									}}
								>
									{activeEventFilter?.title || 'Show all events'}
								</Button>
								{isEventDropdownOpen && (
									<div
										ref={eventsFilterRef}
										className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white text-sm"
									>
										<ul>
											<li
												className={clsx(
													'cursor-pointer px-4 py-2 text-gray-500 hover:bg-gray-100',
													!activeEventFilter && 'bg-blue-50'
												)}
												onClick={() => {
													setActiveEventFilter(undefined);
													setIsEventDropdownOpen(false);
												}}
											>
												Show all events
											</li>
											{initialEventsData?.map((group, i) => (
												<div key={i}>
													<li className="cursor-default px-4 py-2 font-bold text-gray-700">{group.groupTitle}</li>
													{group.events.map((event) => (
														<li
															key={event.activityId + event.activitySplitId}
															className={clsx(
																'cursor-pointer px-4 py-2 text-gray-500 hover:bg-gray-100',
																activeEventFilter === event && 'bg-blue-50'
															)}
															onClick={() => {
																setActiveEventFilter(event);
																setIsEventDropdownOpen(false);
															}}
														>
															{event.title}
														</li>
													))}
												</div>
											))}
										</ul>
									</div>
								)}
							</div>
						</div>
						{filterStatuses?.length > 0 && (
							<div className="w-full md:w-1/3">
								<div className="relative">
									<Button
										variant="link-gray"
										suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
										className="w-full justify-between rounded-lg border border-gray-300 bg-white p-2 text-left text-sm font-normal text-gray-500"
										onClick={() => {
											setIsStatusDropdownOpen(!isStatusDropdownOpen);
											setIsEventDropdownOpen(false);
											setIsDatesDropdownOpen(false);
										}}
									>
										{activeStatusFilter || 'Show all statuses'}
									</Button>
									{isStatusDropdownOpen && (
										<div
											ref={statusFilterRef}
											className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white text-sm"
										>
											<ul>
												<li
													className={clsx(
														'cursor-pointer px-4 py-2 text-gray-500 hover:bg-gray-100',
														!activeStatusFilter && 'bg-blue-50'
													)}
													onClick={() => {
														setActiveStatusFilter('');
														setIsStatusDropdownOpen(false);
													}}
												>
													Show all statuses
												</li>

												{filterStatuses?.map((status, index) => (
													<li
														key={index}
														className={clsx(
															'cursor-pointer px-4 py-2 text-gray-500 hover:bg-gray-100',
															activeStatusFilter === status && 'bg-blue-50'
														)}
														onClick={() => {
															setActiveStatusFilter(status);
															setIsStatusDropdownOpen(false);
														}}
													>
														{status}
													</li>
												))}
											</ul>
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					{filteredEventsData?.map((group: EventGroup, i) => {
						return (
							<div key={i} className="flex flex-col gap-4">
								<div className="rounded-lg bg-blue-600 p-2 font-semibold text-white ">{group.groupTitle}</div>
								{group.events.map((event: TournamentEvent) => (
									<Event
										tourneyPlayersHidden={tourneyPlayersHidden}
										isManager={isManagerDetails.IsManager}
										tourneyId={tourneyId}
										key={event.activityId + event.activitySplitId}
										needAPartnerOnly={needAPartnerOnly}
										data={event}
										setBracketModalOpen={setBracketModalOpen}
									/>
								))}
							</div>
						);
					})}
				</div>
			)}
			<Modal onOpenChange={() => setBracketModalOpen(!bracketModalOpen)} open={bracketModalOpen} backdropClose={false}>
				<ModalContent className="flex w-[400px] flex-col justify-center">
					<ModalHeading className="flex w-full items-center justify-between">
						<div className="flex-1 text-xl font-semibold capitalize leading-7 text-gray-900">Draws Status</div>
						<ModalClose className="ml-auto">
							<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500" />
						</ModalClose>
					</ModalHeading>
					<ModalDescription className="mt-4 text-base font-normal text-gray-600">Draws not available at this time.</ModalDescription>
				</ModalContent>
			</Modal>
		</>
	);
};
